import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { getCollageTimeline, getCollageSingle } from '../../../http/complexesApi';
import { unixTimeToString } from '../../../utils/converters';
import { OrbitProgress } from 'react-loading-indicators';
import '../../../styles/ComplexImages.css';

// Material-UI Core
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    Tooltip,
    Stack,
    Avatar,
    LinearProgress,
    Fade,
    Slide,
    Zoom
} from '@mui/material';

// Material-UI Icons
import {
    AccessTime as AccessTimeIcon,
    ArrowBackIos as ArrowBackIosIcon,
    ArrowForwardIos as ArrowForwardIosIcon,
    PlayCircleFilled as PlayCircleFilledIcon,
    Image as ImageIcon,
    Videocam as VideocamIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    Keyboard as KeyboardIcon,
    TouchApp as TouchAppIcon,
    WarningAmber as WarningAmberIcon,
    ErrorOutline as ErrorOutlineIcon,
    PlayArrow as PlayArrowIcon,
    FiberManualRecord as FiberManualRecordIcon,
    Schedule as ScheduleIcon,
    PhotoLibrary as PhotoLibraryIcon,
    VideoLibrary as VideoLibraryIcon
} from '@mui/icons-material';

// Material-UI Theme
import { ThemeProvider, createTheme } from '@mui/material/styles';

// Создаем кастомную тему
const theme = createTheme({
    palette: {
        primary: {
            main: '#4f46e5',
            light: '#7c3aed',
            dark: '#3730a3'
        },
        secondary: {
            main: '#f59e0b',
            light: '#fbbf24',
            dark: '#d97706'
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff'
        }
    },
    shape: {
        borderRadius: 12
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
    }
});

const ComplexCollage = ({ complex }) => {
    const [timeline, setTimeline] = useState([]);
    const [selectedTime, setSelectedTime] = useState(null);
    const [mediaData, setMediaData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [timelineLoading, setTimelineLoading] = useState(true);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [currentMediaType, setCurrentMediaType] = useState('images');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const timelineRef = useRef();
    const mediaContainerRef = useRef();

    const server = useMemo(() => {
        return complex.macros.find(macro => macro.macro === '{$ODBC_BASE}')?.value
    }, [complex])

    const crossroad = useMemo(() => {
        return complex.name.split('/')[1].trim()
    }, [complex])

    const currentMediaArray = useMemo(() => {
        if (!mediaData) return [];
        return currentMediaType === 'images' ? (mediaData.images || []) : (mediaData.videos || []);
    }, [mediaData, currentMediaType]);

    const handleKeyPress = useCallback((event) => {
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return;
        
        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                if (event.shiftKey) {
                    navigateTime('prev');
                } else {
                    navigateMedia('prev');
                }
                break;
            case 'ArrowRight':
                event.preventDefault();
                if (event.shiftKey) {
                    navigateTime('next');
                } else {
                    navigateMedia('next');
                }
                break;
            case '1':
                handleMediaTypeChange('images');
                break;
            case '2':
                handleMediaTypeChange('videos');
                break;
            case 'f':
            case 'F':
                toggleFullscreen();
                break;
            case 'Escape':
                if (isFullscreen) {
                    exitFullscreen();
                }
                break;
            default:
                break;
        }
    }, [isFullscreen]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress);
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleKeyPress]);

    useEffect(() => {
        const loadTimeline = async () => {
            try {
                setTimelineLoading(true);
                const timelineData = await getCollageTimeline(server, crossroad);
                console.log(timelineData)
                timelineData.sort((a, b) => b - a)
                setTimeline(timelineData);
                
                if (timelineData && timelineData.length > 0) {
                    setSelectedTime(timelineData[0]);
                }
            } catch (error) {
                console.error('Ошибка загрузки временной шкалы:', error);
                setTimeline([]);
            } finally {
                setTimelineLoading(false);
            }
        };

        loadTimeline();
    }, []);

    useEffect(() => {
        if (selectedTime) {
            const loadMediaData = async () => {
                try {
                    setLoading(true);
                    const media = await getCollageSingle(server, crossroad, selectedTime);
                    setMediaData(media);
                    console.log(media)
                    setCurrentMediaIndex(0);
                } catch (error) {
                    console.error('Ошибка загрузки медиа данных:', error);
                    setMediaData(null);
                } finally {
                    setLoading(false);
                }
            };

            loadMediaData();
        }
    }, [selectedTime]);

    useEffect(() => {
        setCurrentMediaIndex(0);
    }, [currentMediaType]);

    const handleTimeSelect = (time) => {
        setSelectedTime(time);
    };

    const navigateTime = (direction) => {
        if (!timeline.length || !selectedTime) return;
        
        const currentIndex = timeline.indexOf(selectedTime);
        let newIndex;
        
        if (direction === 'prev' && currentIndex > 0) {
            newIndex = currentIndex - 1;
        } else if (direction === 'next' && currentIndex < timeline.length - 1) {
            newIndex = currentIndex + 1;
        } else {
            return;
        }
        
        setSelectedTime(timeline[newIndex]);
    };

    const navigateMedia = (direction) => {
        if (!currentMediaArray.length) return;
        
        let newIndex;
        if (direction === 'prev' && currentMediaIndex > 0) {
            newIndex = currentMediaIndex - 1;
        } else if (direction === 'next' && currentMediaIndex < currentMediaArray.length - 1) {
            newIndex = currentMediaIndex + 1;
        } else {
            return;
        }
        
        setCurrentMediaIndex(newIndex);
    };

    const handleMediaTypeChange = (type) => {
        setCurrentMediaType(type);
    };

    const toggleFullscreen = () => {
        if (!isFullscreen) {
            enterFullscreen();
        } else {
            exitFullscreen();
        }
    };

    const enterFullscreen = () => {
        if (mediaContainerRef.current) {
            if (mediaContainerRef.current.requestFullscreen) {
                mediaContainerRef.current.requestFullscreen();
            } else if (mediaContainerRef.current.webkitRequestFullscreen) {
                mediaContainerRef.current.webkitRequestFullscreen();
            } else if (mediaContainerRef.current.msRequestFullscreen) {
                mediaContainerRef.current.msRequestFullscreen();
            }
            setIsFullscreen(true);
        }
    };

    const exitFullscreen = () => {
        if (document.fullscreenElement) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
        setIsFullscreen(false);
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    if (timelineLoading) {
        return (
            <ThemeProvider theme={theme}>
                <Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: 400,
                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
                    }}
                >
                    <OrbitProgress variant="track-disc" dense color="#4f46e5" size="medium" />
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ display: 'flex', gap: 2, height: '100vh', maxHeight: '800px', p: 2 }}>
                {/* Вертикальная временная шкала слева */}
                <Paper 
                    elevation={4}
                    sx={{ 
                        width: 280,
                        background: 'linear-gradient(180deg, #4f46e5 0%, #7c3aed 100%)',
                        color: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    {/* Заголовок временной шкалы */}
                    <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <AccessTimeIcon sx={{ fontSize: 20 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px' }}>
                                Временная шкала
                            </Typography>
                        </Stack>
                        
                        {/* Навигация по времени */}
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title="Предыдущее время (Shift + ←)">
                                <IconButton 
                                    size="small"
                                    onClick={() => navigateTime('prev')}
                                    disabled={!timeline.length || timeline.indexOf(selectedTime) === 0}
                                    sx={{ 
                                        color: 'white',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                    }}
                                >
                                    <ArrowBackIosIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Следующее время (Shift + →)">
                                <IconButton 
                                    size="small"
                                    onClick={() => navigateTime('next')}
                                    disabled={!timeline.length || timeline.indexOf(selectedTime) === timeline.length - 1}
                                    sx={{ 
                                        color: 'white',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                    }}
                                >
                                    <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                            
                            <Typography variant="caption" sx={{ fontSize: '11px', opacity: 0.8 }}>
                                Shift + ←/→
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Список времен */}
                    <Box 
                        ref={timelineRef}
                        sx={{ 
                            flex: 1,
                            overflowY: 'auto',
                            p: 1,
                            '&::-webkit-scrollbar': {
                                width: '6px'
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '3px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(255,255,255,0.3)',
                                borderRadius: '3px',
                                '&:hover': {
                                    background: 'rgba(255,255,255,0.5)'
                                }
                            }
                        }}
                    >
                        {timeline.length > 0 ? (
                            <Stack spacing={1}>
                                {timeline.map((time, index) => (
                                    <Zoom 
                                        key={time} 
                                        in={true} 
                                        timeout={200 + index * 50}
                                        style={{ transitionDelay: `${index * 50}ms` }}
                                    >
                                        <Paper
                                            elevation={selectedTime === time ? 8 : 2}
                                            onClick={() => handleTimeSelect(time)}
                                            sx={{
                                                p: 1,
                                                cursor: 'pointer',
                                                background: selectedTime === time 
                                                    ? 'rgba(255,255,255,0.95)' 
                                                    : 'rgba(255,255,255,0.1)',
                                                color: selectedTime === time ? '#4f46e5' : 'white',
                                                border: selectedTime === time ? '2px solid #fbbf24' : '1px solid rgba(255,255,255,0.2)',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                transform: selectedTime === time ? 'scale(1.02)' : 'scale(1)',
                                                '&:hover': {
                                                    background: selectedTime === time 
                                                        ? 'rgba(255,255,255,0.95)' 
                                                        : 'rgba(255,255,255,0.15)',
                                                    transform: 'scale(1.02)',
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                                }
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Avatar 
                                                    sx={{ 
                                                        width: 32, 
                                                        height: 32,
                                                        background: selectedTime === time 
                                                            ? 'linear-gradient(135deg, #4f46e5, #7c3aed)' 
                                                            : 'rgba(255,255,255,0.2)',
                                                        color: selectedTime === time ? 'white' : 'inherit',
                                                        fontSize: '12px',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {index + 1}
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            fontWeight: selectedTime === time ? 600 : 400,
                                                            fontSize: '13px',
                                                            mb: 0
                                                        }}
                                                    >
                                                        {unixTimeToString(time).split(' ')[1]}
                                                    </Typography>
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            opacity: 0.8,
                                                            fontSize: '11px'
                                                        }}
                                                    >
                                                        {unixTimeToString(time).split(' ')[0]}
                                                    </Typography>
                                                </Box>
                                                {selectedTime === time && (
                                                    <FiberManualRecordIcon 
                                                        sx={{ 
                                                            fontSize: 12, 
                                                            color: '#fbbf24',
                                                            animation: 'pulse 2s infinite'
                                                        }} 
                                                    />
                                                )}
                                            </Stack>
                                        </Paper>
                                    </Zoom>
                                ))}
                            </Stack>
                        ) : (
                            <Box sx={{ textAlign: 'center', mt: 4 }}>
                                <ErrorOutlineIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    Временная шкала не найдена
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Прогресс бар */}
                    {timeline.length > 0 && (
                        <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                            <LinearProgress 
                                variant="determinate" 
                                value={selectedTime ? (timeline.indexOf(selectedTime) / (timeline.length - 1)) * 100 : 0}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    '& .MuiLinearProgress-bar': {
                                        background: 'linear-gradient(90deg, #fbbf24, #f59e0b)',
                                        borderRadius: 3
                                    }
                                }}
                            />
                            <Typography variant="caption" sx={{ fontSize: '10px', opacity: 0.8, mt: 0.5, display: 'block' }}>
                                {selectedTime ? timeline.indexOf(selectedTime) + 1 : 0} из {timeline.length}
                            </Typography>
                        </Box>
                    )}
                </Paper>

                {/* Медиа проигрыватель справа */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {selectedTime ? (
                        <Fade in={true}>
                            <Paper 
                                elevation={6}
                                sx={{ 
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                                }}
                            >
                                {/* Заголовок проигрывателя */}
                                <CardHeader
                                    avatar={
                                        <PlayCircleFilledIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                                    }
                                    title={
                                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px' }}>
                                            Медиа проигрыватель
                                        </Typography>
                                    }
                                    subheader={unixTimeToString(selectedTime)}
                                    action={
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip 
                                                icon={<KeyboardIcon />}
                                                label="←/→ медиа, F - полный экран"
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '10px' }}
                                            />
                                            
                                            <ButtonGroup size="small" variant="outlined">
                                                <Button
                                                    variant={currentMediaType === 'images' ? 'contained' : 'outlined'}
                                                    onClick={() => handleMediaTypeChange('images')}
                                                    startIcon={<ImageIcon />}
                                                    sx={{ fontSize: '12px' }}
                                                >
                                                    {mediaData?.images?.length || 0}
                                                </Button>
                                                <Button
                                                    variant={currentMediaType === 'videos' ? 'contained' : 'outlined'}
                                                    onClick={() => handleMediaTypeChange('videos')}
                                                    startIcon={<VideocamIcon />}
                                                    sx={{ fontSize: '12px' }}
                                                >
                                                    {mediaData?.videos?.length || 0}
                                                </Button>
                                            </ButtonGroup>
                                            
                                            <Tooltip title="Полный экран (F)">
                                                <IconButton onClick={toggleFullscreen} color="primary">
                                                    {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    }
                                    sx={{ 
                                        borderBottom: '1px solid #e5e7eb',
                                        '& .MuiCardHeader-content': { overflow: 'hidden' }
                                    }}
                                />

                                <CardContent sx={{ flex: 1, p: 0, display: 'flex', flexDirection: 'column' }}>
                                    {loading ? (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'center', 
                                            alignItems: 'center', 
                                            flex: 1,
                                            minHeight: 400 
                                        }}>
                                            <OrbitProgress variant="track-disc" dense color="#4f46e5" size="medium" />
                                        </Box>
                                    ) : mediaData && currentMediaArray.length > 0 ? (
                                        <>
                                            {/* Основное медиа */}
                                            <Box 
                                                ref={mediaContainerRef}
                                                sx={{
                                                    flex: 1,
                                                    position: 'relative',
                                                    background: '#000',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minHeight: 400,
                                                    ...(isFullscreen && {
                                                        position: 'fixed',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100vw',
                                                        height: '100vh',
                                                        zIndex: 9999
                                                    })
                                                }}
                                            >
                                                {currentMediaType === 'images' ? (
                                                    <Fade in={true} timeout={500}>
                                                        <Box
                                                            component="img"
                                                            src={`${currentMediaArray[currentMediaIndex]?.data}`}
                                                            alt={`Изображение ${currentMediaIndex + 1}`}
                                                            sx={{
                                                                maxHeight: '100%',
                                                                maxWidth: '100%',
                                                                objectFit: 'contain',
                                                                borderRadius: isFullscreen ? 0 : 2
                                                            }}
                                                        />
                                                    </Fade>
                                                ) : (
                                                    <Fade in={true} timeout={500}>
                                                        <Box
                                                            component="video"
                                                            controls
                                                            key={currentMediaIndex}
                                                            sx={{
                                                                maxHeight: '100%',
                                                                maxWidth: '100%',
                                                                borderRadius: isFullscreen ? 0 : 2
                                                            }}
                                                        >
                                                            <source src={`${currentMediaArray[currentMediaIndex]?.data}`} type="video/mp4" />
                                                            Ваш браузер не поддерживает воспроизведение видео.
                                                        </Box>
                                                    </Fade>
                                                )}

                                                {/* Навигация */}
                                                {currentMediaArray.length > 1 && (
                                                    <>
                                                        <Tooltip title="Предыдущий файл (←)">
                                                            <IconButton
                                                                onClick={() => navigateMedia('prev')}
                                                                disabled={currentMediaIndex === 0}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    left: isFullscreen ? 32 : 16,
                                                                    top: '50%',
                                                                    transform: 'translateY(-50%)',
                                                                    background: 'rgba(0,0,0,0.7)',
                                                                    color: 'white',
                                                                    width: isFullscreen ? 60 : 48,
                                                                    height: isFullscreen ? 60 : 48,
                                                                    '&:hover': {
                                                                        background: 'rgba(0,0,0,0.9)',
                                                                        transform: 'translateY(-50%) scale(1.1)'
                                                                    },
                                                                    '&:disabled': {
                                                                        opacity: 0.3
                                                                    }
                                                                }}
                                                            >
                                                                <ChevronLeftIcon sx={{ fontSize: isFullscreen ? 32 : 24 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        
                                                        <Tooltip title="Следующий файл (→)">
                                                            <IconButton
                                                                onClick={() => navigateMedia('next')}
                                                                disabled={currentMediaIndex === currentMediaArray.length - 1}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    right: isFullscreen ? 32 : 16,
                                                                    top: '50%',
                                                                    transform: 'translateY(-50%)',
                                                                    background: 'rgba(0,0,0,0.7)',
                                                                    color: 'white',
                                                                    width: isFullscreen ? 60 : 48,
                                                                    height: isFullscreen ? 60 : 48,
                                                                    '&:hover': {
                                                                        background: 'rgba(0,0,0,0.9)',
                                                                        transform: 'translateY(-50%) scale(1.1)'
                                                                    },
                                                                    '&:disabled': {
                                                                        opacity: 0.3
                                                                    }
                                                                }}
                                                            >
                                                                <ChevronRightIcon sx={{ fontSize: isFullscreen ? 32 : 24 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}

                                                {/* Информация в полноэкранном режиме */}
                                                {isFullscreen && (
                                                    <Slide direction="up" in={true} timeout={500}>
                                                        <Paper
                                                            sx={{
                                                                position: 'absolute',
                                                                bottom: 0,
                                                                left: 0,
                                                                right: 0,
                                                                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                                                                color: 'white',
                                                                p: 3,
                                                                borderRadius: 0
                                                            }}
                                                        >
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                                    {currentMediaType === 'images' ? 
                                                                        <ImageIcon sx={{ fontSize: 20 }} /> : 
                                                                        <VideocamIcon sx={{ fontSize: 20 }} />
                                                                    }
                                                                    <Typography variant="body1">
                                                                        {currentMediaType === 'images' ? 'Изображение' : 'Видео'} {currentMediaIndex + 1} из {currentMediaArray.length}
                                                                    </Typography>
                                                                </Stack>
                                                                <Box sx={{ textAlign: 'right' }}>
                                                                    <Typography variant="body1">
                                                                        {unixTimeToString(selectedTime)}
                                                                    </Typography>
                                                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                                        ESC - выход, ←/→ - навигация
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>
                                                        </Paper>
                                                    </Slide>
                                                )}
                                            </Box>

                                            {/* Информация о медиа (только не в полноэкранном режиме) */}
                                            {!isFullscreen && (
                                                <Box sx={{ p: 2, borderTop: '1px solid #e5e7eb', background: 'rgba(248,250,252,0.8)' }}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            {currentMediaType === 'images' ? 
                                                                <PhotoLibraryIcon color="primary" sx={{ fontSize: 18 }} /> : 
                                                                <VideoLibraryIcon color="primary" sx={{ fontSize: 18 }} />
                                                            }
                                                            <Typography variant="body2" color="text.secondary">
                                                                {currentMediaType === 'images' ? 'Изображение' : 'Видео'} {currentMediaIndex + 1} из {currentMediaArray.length}
                                                            </Typography>
                                                        </Stack>
                                                        
                                                        {/* Миниатюры */}
                                                        {currentMediaArray.length > 1 && (
                                                            <Stack direction="row" spacing={1}>
                                                                {currentMediaArray.slice(0, 4).map((media, index) => (
                                                                    <Tooltip 
                                                                        key={index} 
                                                                        title={`${currentMediaType === 'images' ? 'Изображение' : 'Видео'} ${index + 1}`}
                                                                    >
                                                                        <Paper
                                                                            elevation={index === currentMediaIndex ? 8 : 2}
                                                                            onClick={() => setCurrentMediaIndex(index)}
                                                                            sx={{
                                                                                width: 50,
                                                                                height: 35,
                                                                                overflow: 'hidden',
                                                                                cursor: 'pointer',
                                                                                border: index === currentMediaIndex ? '2px solid' : '1px solid',
                                                                                borderColor: index === currentMediaIndex ? 'primary.main' : 'grey.300',
                                                                                transition: 'all 0.2s ease',
                                                                                '&:hover': {
                                                                                    transform: 'translateY(-2px)',
                                                                                    boxShadow: 4
                                                                                }
                                                                            }}
                                                                        >
                                                                            {currentMediaType === 'images' ? (
                                                                                <Box
                                                                                    component="img"
                                                                                    src={`${media.data}`}
                                                                                    alt={`Миниатюра ${index + 1}`}
                                                                                    sx={{
                                                                                        width: '100%',
                                                                                        height: '100%',
                                                                                        objectFit: 'cover'
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <Box
                                                                                    sx={{
                                                                                        width: '100%',
                                                                                        height: '100%',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        background: '#000',
                                                                                        color: 'white'
                                                                                    }}
                                                                                >
                                                                                    <PlayArrowIcon sx={{ fontSize: 16 }} />
                                                                                </Box>
                                                                            )}
                                                                        </Paper>
                                                                    </Tooltip>
                                                                ))}
                                                                {currentMediaArray.length > 4 && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                                                        +{currentMediaArray.length - 4}
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        )}
                                                    </Stack>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">  
                                                        <Typography variant="body2" color="text.secondary">
                                                            {currentMediaArray[currentMediaIndex]?.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {(currentMediaArray[currentMediaIndex]?.size / 1024 / 1024).toFixed(2)} MB
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            )}
                                        </>
                                    ) : (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            flexDirection: 'column',
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            flex: 1,
                                            minHeight: 400,
                                            textAlign: 'center',
                                            p: 4
                                        }}>
                                            <WarningAmberIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
                                            <Typography variant="h5" gutterBottom>
                                                Медиа данные не найдены
                                            </Typography>
                                            <Typography variant="body1" color="text.secondary" paragraph>
                                                Для выбранного времени нет доступных {currentMediaType === 'images' ? 'изображений' : 'видео'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Попробуйте выбрать другое время или проверьте подключение к серверу
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Paper>
                        </Fade>
                    ) : (
                        <Fade in={true}>
                            <Paper 
                                elevation={4}
                                sx={{ 
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    p: 4,
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                                }}
                            >
                                <TouchAppIcon sx={{ fontSize: 80, color: 'primary.main', mb: 3 }} />
                                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                                    Выберите время для просмотра
                                </Typography>
                                <Typography variant="body1" color="text.secondary" paragraph>
                                    Кликните на любую временную метку слева, чтобы загрузить медиа данные
                                </Typography>
                                <Chip 
                                    icon={<KeyboardIcon />}
                                    label="Используйте клавиши Shift + ←/→ для навигации по времени"
                                    variant="outlined"
                                    sx={{ mt: 2 }}
                                />
                            </Paper>
                        </Fade>
                    )}
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default ComplexCollage;
